import { mdiArrowLeft, mdiClose, mdiInformationOutline } from '@mdi/js';
import { Button, Divider, IconButton, Stack, SxProps } from '@mui/material';
import React, { Dispatch, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { updateSelectedDocuments } from '../../../redux/project/documents/sliceProjectSelectedDocuments';
import { IAppReduxStore } from '../../../redux/store';
import { headingFontBold, vars } from '../../../Theme';
import ActionButton from '../../General/ActionButton/ActionButton';
import AppIcon from '../../General/Icons/AppIcon';
import { useParams } from 'react-router-dom';
import { getAreasExtendedConfigByField } from '../../../redux/project/sliceProjectAreas';
import {
    getActionGroups,
    getPermittedActions,
    getActionsListGroupByActionGroup,
} from '../../../redux/project/sliceProjectActions';
import Icon from '@mdi/react';
import SnackbarActions from '../../General/SnackbarActions/SnackbarActions';
import dayjs from 'dayjs';
import { getCurrentView } from '../../../redux/project/sliceProjectViews';

/** Styles for the main component Stack element. */
const stackSx: SxProps = {
    boxShadow: '0px 1px 3px 0px rgba(0, 0, 0, 0.30), 0px 4px 8px 3px rgba(0, 0, 0, 0.15)',
    position: 'absolute',
    whiteSpace: 'nowrap',
    bgcolor: vars.white,
    translate: '-50%',
    borderRadius: 1,
    zIndex: 1000,
    left: '50%',
    bottom: 16,
    py: 0.5,
    px: 2,
};

/** Styles for the toggle button. */
const toggleButtonSx: SxProps = {
    ...headingFontBold,
    textTransform: 'none',
    fontSize: 12,
    minWidth: 0,
    p: 0,
    '&:hover': {
        backgroundColor: 'unset',
    },
};

/**
 * Component that shows summary information on the selected docments in a project.
 */
export default ({
    onClick,
    showBackButton = false,
}: {
    onClick?: React.MouseEventHandler;
    showBackButton?: boolean;
}) => {
    /** Allows reducers on a redux slice to be called. */
    const dispatch: Dispatch<unknown> = useDispatch();

    /** Configure component translation. */
    const { i18n, t } = useTranslation('translations', { keyPrefix: 'projectView' });

    /** Get the replicaId from the react router. */
    const { replicaId } = useParams();

    /** Get the  areaName from the react router. */
    const { areaName, viewName } = useParams();
    const currentDay = dayjs().format('DDMMYYYY');

    /** Access relevant state variables from the redux store. */
    const areas = useSelector((store: IAppReduxStore) => store.projectAreas.data);
    const currentView = useSelector(getCurrentView({ areaName, viewName }));
    const selectedDocuments = useSelector(
        (store: IAppReduxStore) => store.projectSelectedDocuments.storage,
    );
    const actionsList = useSelector(getPermittedActions);
    const actionsGroupsList = useSelector(getActionGroups);
    const availableSnackbarActions = useSelector(
        getAreasExtendedConfigByField(areaName, 'SnackbarActions'),
    );

    /** Access max number of documents allowed to select from the redux store*/
    const maxAllowedToSelect = useSelector(
        (store: IAppReduxStore) => store.projectSelectedDocuments.maxAllowedToSelect,
    );
    /** Divider hat is used by the component to define areas of the component. */
    const divider = <Divider orientation='vertical' color={vars.lightGray} flexItem />;

    /** Calculate the number of selected documents. */
    const totalDocuments = selectedDocuments?.documentIds?.length;

    /**
     * Returns availiable action's list for the snackbar menu.
     */
    const getAvailableSnackbarActionsList = useMemo(() => {
        if (!actionsList || !availableSnackbarActions) return null;

        const availableSnackbarActionsList = availableSnackbarActions
            .map(action => actionsList.find(item => item.name === action))
            .filter(Boolean);

        if (!actionsGroupsList) return availableSnackbarActionsList;
        return getActionsListGroupByActionGroup(availableSnackbarActionsList, actionsGroupsList);
    }, [availableSnackbarActions, actionsList, actionsGroupsList]);

    const createReportsFileName = useMemo(() => {
        if (!areas) return;
        const area = areas[areaName]?.translations?.[i18n.language] ?? '';
        const view = currentView?.translations?.[i18n.language] ?? '';
        return `${currentDay}_${area}_${view}_EPLASS_Report`;
    }, [areaName, currentView, currentDay, areas, i18n.language]);

    /**
     * Clears all selected documents.
     */
    const clear = () => {
        dispatch(
            updateSelectedDocuments({
                replicaId: selectedDocuments?.replicaId,
                areaName: selectedDocuments?.areaName,
                documentIds: [],
            }),
        );
    };

    if (!totalDocuments) {
        return <></>;
    }

    return (
        <Stack
            direction='row'
            divider={divider}
            sx={[
                {
                    alignItems: 'center',
                },
                ...(Array.isArray(stackSx) ? stackSx : [stackSx]),
            ]}
        >
            <Stack direction='row' sx={{ pr: 2 }}>
                <Stack
                    direction='row'
                    sx={{
                        alignItems: 'start',
                        pr: 0,
                        py: 0.5,
                    }}
                >
                    <Stack
                        sx={{
                            ...headingFontBold,
                            fontSize: '24px',
                            fontStyle: 'normal',
                            fontWeight: 700,
                            lineHeight: 'normal',
                            textAlign: 'center',
                            alignSelf: 'center',
                            color: totalDocuments > maxAllowedToSelect && vars.brandDarkBlue,
                        }}
                    >
                        {totalDocuments}
                    </Stack>

                    <Stack sx={{ alignSelf: 'center' }}>
                        <Stack direction={'row'} sx={{ pl: 2 }}>
                            <Stack sx={{ ...headingFontBold, fontSize: 14 }}>
                                {t(totalDocuments > 1 ? 'documentsSelected' : 'documentSelected')}
                            </Stack>
                            {totalDocuments > maxAllowedToSelect && (
                                <Stack sx={{ marginLeft: 1 }}>
                                    <AppIcon
                                        path={mdiInformationOutline}
                                        color={vars.brandDarkBlue}
                                        tooltipText={t('selectedMoreThanMaxAllowed', {
                                            number: maxAllowedToSelect,
                                        })}
                                        tooltipPlacement='top-start'
                                    />
                                </Stack>
                            )}
                        </Stack>

                        {!showBackButton && areaName !== 'import' && (
                            <Stack
                                sx={{
                                    alignItems: 'flex-start',
                                    pl: 2,
                                }}
                            >
                                <Button
                                    sx={toggleButtonSx}
                                    onClick={onClick}
                                    disabled={totalDocuments > maxAllowedToSelect}
                                >
                                    {t('show')}
                                </Button>
                            </Stack>
                        )}
                    </Stack>
                </Stack>
                {showBackButton && (
                    <Stack
                        direction='row'
                        sx={{
                            flexGrow: 0,
                            flexShrink: 0,
                            pl: 2,
                        }}
                    >
                        <ActionButton
                            icon={mdiArrowLeft}
                            text={t('back')}
                            onClick={onClick}
                            disabled={totalDocuments > maxAllowedToSelect}
                        />
                    </Stack>
                )}
            </Stack>

            <Stack
                direction='row'
                className='actions'
                sx={{
                    gap: 2,
                    px: getAvailableSnackbarActionsList ? 1 : 0,
                    paddingX: 2,
                }}
            >
                <SnackbarActions
                    replicaId={replicaId}
                    documentIds={selectedDocuments?.documentIds}
                    currentView={currentView}
                    availableSnackbarActionsList={getAvailableSnackbarActionsList}
                    reportName={createReportsFileName}
                />
            </Stack>

            <Stack
                direction='row'
                sx={{
                    flexGrow: 0,
                    flexShrink: 0,
                    pl: 1,
                }}
            >
                <IconButton sx={{ p: 0 }} aria-label='clear selection' onClick={clear}>
                    <Icon color={vars.font} path={mdiClose} size={1} />
                </IconButton>
            </Stack>
        </Stack>
    );
};
